import React, { useEffect, useMemo, useState } from "react";
import { RadarAvatar } from "../agent-radar/RadarAtoms";
import { clubRadarService } from "../../services/clubRadarService";
import { CLUB_SHARE_TIERS } from "../../constants/clubRadarChoices";
import {
  ModalShell,
  ModalFooter,
  BottomSheetShell,
  CrRadio,
  PrimaryButton,
  SecondaryButton,
  TextButton,
  IconSquareButton,
  GLYPH,
} from "./v2";

// Manage-sharing sheet (frames 2a desktop modal / 2b mobile bottom sheet, R4).
// Three tiers — Personal · Selected people · Whole club — set here, SEPARATE from
// Share-to-chat (R5). "Selected people" reveals a searchable colleague
// multi-select via lists/{id}/members/. No per-person / per-role rights: everyone
// with access has uniform capabilities (info-panel copy, R6). Creator/ClubAdmin
// only (the ⋯ gate upstream).
//
// V2 rebuild: chrome (modal/sheet shells, tier rows, info panel, footers) is
// 1:1 from the artboard. The MEMBER SELECTION block (search input + member
// rows) is the platform's existing standard picker, kept exactly as built —
// binding decision D4 (Marin, 2026-07-24) overrides the artboard's redesigned
// picker rows. Same props contract as before: { list, clubName, mobile,
// onClose, onSave }.

// ── D4: platform-standard member picker — kept exactly as built ──────────────
const MemberRow = ({ m, checked, onToggle }) => (
  <button
    type="button"
    onClick={() => onToggle(m.id)}
    className="w-full text-left cursor-pointer"
    style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 4px", background: "none", border: "none" }}
  >
    <RadarAvatar name={m.name} imageUrl={m.avatar_url} size={32} seed={m.initials || m.name} fontSize={11} />
    <span style={{ minWidth: 0, flex: 1 }}>
      <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.name}</span>
      {m.role_label && <span style={{ display: "block", fontSize: 11, color: "#6B7280" }}>{m.role_label}</span>}
    </span>
    <span
      style={{
        width: 20,
        height: 20,
        borderRadius: 6,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 11,
        fontWeight: 800,
        color: "#FFFFFF",
        background: checked ? "#2283FF" : "#FFFFFF",
        border: checked ? "1px solid #2283FF" : "1px solid #C7D0DC",
      }}
    >
      {checked ? "✓" : ""}
    </span>
  </button>
);

// ── Tier option row (2a/2b) — radio + label + blurb; the selected tier gets
// the artboard's blue treatment; "Selected people" additionally expands the
// member picker below its header.
const TierOption = ({ tier, checked, onSelect, clubName, count, mobile, children }) => {
  const blurb = tier.key === "club" ? `Everyone at ${clubName || "your club"} can see and work on this list` : tier.blurb;
  const header = (
    <button
      type="button"
      onClick={() => onSelect(tier.key)}
      className="w-full text-left cursor-pointer"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 11,
        padding: mobile ? "11px 12px" : "12px 14px",
        border: "none",
        background: checked ? "#E8F1FF" : "#fff",
        fontFamily: "inherit",
      }}
    >
      <CrRadio selected={checked} size={mobile ? 19 : 18} style={{ marginTop: 1 }} />
      <span style={{ minWidth: 0, flex: 1 }}>
        <span style={{ display: "block", fontSize: 13.5, fontWeight: 700, color: "#111827" }}>{tier.label}</span>
        <span style={{ display: "block", fontSize: mobile ? 11 : 11.5, color: checked ? "#1B6FE0" : "#9CA3AF", marginTop: 1 }}>{blurb}</span>
      </span>
      {tier.key === "selected" && count > 0 && (
        <span style={{ marginLeft: "auto", flex: "none", fontSize: mobile ? 11 : 11.5, fontWeight: 700, color: "#1B6FE0" }}>{count} selected</span>
      )}
    </button>
  );
  return (
    <div
      style={{
        border: checked ? "1.5px solid #2283FF" : "1px solid #E5E7EB",
        borderRadius: 12,
        overflow: "hidden",
        background: "#fff",
      }}
    >
      {header}
      {children}
    </div>
  );
};

const ClubManageSharingSheet = ({ list, clubName, mobile, onClose, onSave }) => {
  const [tier, setTier] = useState(list.share_tier || "personal");
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    clubRadarService.getListMembers(list.id, controller.signal).then((res) => {
      if (!alive || !res.success) return;
      setMembers(res.data);
      setSelected(res.data.filter((m) => m.selected).map((m) => m.id));
    }).catch(() => {});
    return () => {
      alive = false;
      controller.abort();
    };
  }, [list.id]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return members;
    return members.filter((m) => String(m.name || "").toLowerCase().includes(query));
  }, [members, q]);

  const toggle = (id) => setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : cur.concat(id)));

  const save = async () => {
    setSaving(true);
    await onSave({ shareTier: tier, memberIds: tier === "selected" ? selected : [] });
    setSaving(false);
  };

  // D4: platform-standard member search + rows, exactly as built.
  const memberPicker = (
    <div style={{ padding: mobile ? "8px 10px 10px" : "10px 12px 12px" }}>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search colleagues"
        className="w-full outline-none"
        style={{ height: 36, border: "1px solid #DDE3EB", borderRadius: 9, padding: "0 11px", fontSize: 12.5, marginBottom: 6 }}
      />
      <div style={{ maxHeight: 220, overflowY: "auto" }}>
        {filtered.length === 0 && <div style={{ fontSize: 12, color: "#9CA3AF", padding: "10px 4px" }}>No colleagues with radar access found.</div>}
        {filtered.map((m) => (
          <MemberRow key={m.id} m={m} checked={selected.includes(m.id)} onToggle={toggle} />
        ))}
      </div>
    </div>
  );

  const tierRows = (
    <div style={{ display: "flex", flexDirection: "column", gap: mobile ? 8 : 10 }}>
      {CLUB_SHARE_TIERS.map((t) => (
        <TierOption
          key={t.key}
          tier={t}
          checked={tier === t.key}
          onSelect={setTier}
          clubName={clubName}
          count={selected.length}
          mobile={mobile}
        >
          {t.key === "selected" && tier === "selected" ? memberPicker : null}
        </TierOption>
      ))}
    </div>
  );

  if (mobile) {
    return (
      <BottomSheetShell open onClose={onClose}>
        <div style={{ fontSize: 15, fontWeight: 800, color: "#111827" }}>Sharing</div>
        <div style={{ fontSize: 11.5, color: "#9CA3AF", marginTop: 2 }}>{list.name} {GLYPH.middot} created by you</div>
        <div style={{ marginTop: 12 }}>{tierRows}</div>
        <div style={{ marginTop: 10, fontSize: 10.5, color: "#9CA3AF", lineHeight: 1.5 }}>
          Everyone with access can view, add players, set statuses and write notes {GLYPH.emDash} always attributed.
          Rename, delete, remove players, change sharing: creator only.
        </div>
        <PrimaryButton height={46} radius={12} fontSize={14} onClick={save} disabled={saving} style={{ width: "100%", marginTop: 12, ...(saving ? { opacity: 0.6 } : null) }}>
          Save sharing
        </PrimaryButton>
        <TextButton height={38} onClick={onClose} style={{ width: "100%", marginTop: 8 }}>Cancel</TextButton>
      </BottomSheetShell>
    );
  }

  return (
    <ModalShell open onClose={onClose} width={560} padding="20px 22px">
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: "#111827" }}>Sharing {GLYPH.emDash} {list.name}</div>
          <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>Created by you {GLYPH.middot} club admins can always manage sharing</div>
        </div>
        <IconSquareButton size={30} radius={9} fontSize={15} aria-label="Close" onClick={onClose}>
          {GLYPH.close}
        </IconSquareButton>
      </div>

      <div style={{ marginTop: 14 }}>{tierRows}</div>

      <div style={{ marginTop: 12, background: "#F8FAFC", border: "1px solid #EEF2F6", borderRadius: 10, padding: "10px 12px", fontSize: 11.5, color: "#6B7280", lineHeight: 1.55 }}>
        Everyone with access can <span style={{ fontWeight: 700, color: "#374151" }}>view, add players, set statuses and write notes</span> {GLYPH.emDash} every
        action shows its author and role. Renaming, deleting, removing players and changing sharing stay with the creator.
      </div>

      <ModalFooter marginTop={16}>
        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
        <PrimaryButton onClick={save} disabled={saving} style={saving ? { opacity: 0.6 } : null}>Save sharing</PrimaryButton>
      </ModalFooter>
    </ModalShell>
  );
};

export default ClubManageSharingSheet;
